import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { FiCalendar } from 'react-icons/fi'

import api from '../../../utils/api'
import Button from '../../../shared/components/Button/Button'
import TimestampToString from '../../../shared/functions/TimestampToString'

import './OrderMarginReport.css'

// Cores fixas por categoria (nunca por posição/ordenação) — a mesma categoria sempre tem a
// mesma cor em qualquer pedido, mesmo que a ordem de exibição mude conforme o valor.
// Paleta categórica padrão do skill de dataviz (8 slots, validada pra CVD/contraste).
const CATEGORY_META = [
    { key: 'tecido', label: 'Tecido', color: '#2a78d6' },
    { key: 'maoDeObra', label: 'Mão de Obra', color: '#eb6834' },
    { key: 'producao', label: 'Custos de Produção', color: '#1baf7a' },
    { key: 'aviamento', label: 'Aviamento', color: '#eda100' },
    { key: 'comissao', label: 'Comissão', color: '#e87ba4' },
    { key: 'acessorios', label: 'Acessórios', color: '#008300' },
    { key: 'aditivos', label: 'Aditivos', color: '#4a3aa7' },
    { key: 'bordadoEstampa', label: 'Bordado / Estampa', color: '#e34948' },
]

const PROFIT_COLOR = '#0ca30c'
const LOSS_COLOR = '#d03b3b'

function money(n) {
    return (n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function pct(value, base) {
    if (!base) return '0.0'
    return ((value / base) * 100).toFixed(1)
}

function OrderMarginReport() {
    const { id } = useParams()
    const [order, setOrder] = useState(null)
    const [margin, setMargin] = useState(null)
    const [load, setLoad] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const { timeStampToDDMMAAAA } = TimestampToString()

    useEffect(() => {
        const token = localStorage.getItem('token') || ''
        api.get(`/production/order/${id}/margin`, { headers: { Authorization: `Bearer ${JSON.parse(token)}` } })
            .then((response) => {
                setOrder(response.data.order)
                setMargin(response.data.margin)
                setLoad(true)
                document.title = `Lucro - Pedido #${response.data.order.code}`
            })
            .catch((err) => {
                setErrorMessage(err.response?.data?.message || 'Não foi possível carregar a margem deste pedido')
                setLoad(true)
            })
    }, [id])

    if (!load) {
        return <div className="pg-loading">Carregando margem do pedido...</div>
    }

    if (errorMessage) {
        return <div className="pg-empty"><p className="pg-empty-title">{errorMessage}</p></div>
    }

    const lines = margin.lines
    const combinedLines = { ...lines, bordadoEstampa: (lines.bordado || 0) + (lines.estampa || 0) }
    const segments = CATEGORY_META
        .map((c) => ({ ...c, value: combinedLines[c.key] || 0 }))
        .filter((s) => s.value > 0)

    const isProfit = margin.profit >= 0
    // Base da barra: quando dá lucro, receita = custos + lucro (100% da barra bate com a
    // receita); quando dá prejuízo, os custos já passam da receita — a barra vira 100% dos
    // custos e o prejuízo aparece só no card, não cabe dividir a mesma barra.
    const barBase = isProfit ? margin.revenue : margin.totalCost

    return (
        <div className="omr-page">
            <div className="omr-toolbar omr-no-print pg-sticky-top">
                <Button variant="print" onClick={() => window.print()}>Imprimir / Exportar PDF</Button>
            </div>

            <div className="omr-print-area">
                <div className="omr-header">
                    <div className="omr-header-title">
                        <p className="omr-title">Lucro Estimado — Pedido #{order.code}</p>
                        <span className="omr-title-accent" />
                    </div>
                    <div className="omr-header-meta">
                        <span>{order.client?.name}</span>
                        {order.orderEntryDate && (
                            <span><FiCalendar size={11} /> {timeStampToDDMMAAAA(order.orderEntryDate)}</span>
                        )}
                    </div>
                </div>

                <div className="omr-kpi-row">
                    <div className="omr-kpi-tile">
                        <span className="omr-kpi-label">Receita</span>
                        <span className="omr-kpi-value">R$ {money(margin.revenue)}</span>
                    </div>
                    <div className="omr-kpi-tile">
                        <span className="omr-kpi-label">Custo total</span>
                        <span className="omr-kpi-value">R$ {money(margin.totalCost)}</span>
                    </div>
                    <div className="omr-kpi-tile">
                        <span className="omr-kpi-label">{isProfit ? 'Lucro estimado' : 'Prejuízo estimado'}</span>
                        <span className="omr-kpi-value" style={{ color: isProfit ? PROFIT_COLOR : LOSS_COLOR }}>
                            R$ {money(Math.abs(margin.profit))}
                        </span>
                    </div>
                </div>

                <div className="omr-chart-section">
                    <p className="omr-section-title">
                        {isProfit ? 'Como a receita se divide entre custo e lucro' : 'Como o custo total se divide (prejuízo não cabe na mesma barra)'}
                    </p>
                    <div className="omr-bar">
                        {barBase > 0 && segments.map((s) => {
                            const widthPct = (s.value / barBase) * 100
                            return (
                                <div
                                    key={s.key}
                                    className="omr-bar-segment"
                                    style={{ width: `${widthPct}%`, background: s.color }}
                                    title={`${s.label}: R$ ${money(s.value)}`}
                                >
                                    {widthPct > 10 && <span className="omr-bar-segment-label">{s.label}</span>}
                                </div>
                            )
                        })}
                        {barBase > 0 && isProfit && (
                            <div
                                className="omr-bar-segment omr-bar-segment-profit"
                                style={{ width: `${(margin.profit / barBase) * 100}%`, background: PROFIT_COLOR }}
                                title={`Lucro: R$ ${money(margin.profit)}`}
                            >
                                {(margin.profit / barBase) * 100 > 10 && <span className="omr-bar-segment-label">Lucro</span>}
                            </div>
                        )}
                    </div>

                    <div className="omr-legend">
                        {segments.map((s) => (
                            <div key={s.key} className="omr-legend-item">
                                <span className="omr-legend-swatch" style={{ background: s.color }} />
                                <span className="omr-legend-label">{s.label}</span>
                                <span className="omr-legend-value">R$ {money(s.value)} · {pct(s.value, margin.revenue)}%</span>
                            </div>
                        ))}
                        <div className="omr-legend-item">
                            <span className="omr-legend-swatch" style={{ background: isProfit ? PROFIT_COLOR : LOSS_COLOR }} />
                            <span className="omr-legend-label">{isProfit ? 'Lucro' : 'Prejuízo'}</span>
                            <span className="omr-legend-value">R$ {money(Math.abs(margin.profit))} · {pct(Math.abs(margin.profit), margin.revenue)}%</span>
                        </div>
                    </div>
                </div>

                <div className="omr-table-section">
                    <p className="omr-section-title">Detalhamento de custos</p>
                    <table className="omr-table">
                        <thead>
                            <tr>
                                <th className="omr-table-left">Linha</th>
                                <th>Valor</th>
                                <th>% da receita</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td className="omr-table-left">Tecido</td>
                                <td>R$ {money(lines.tecido)}</td>
                                <td>{pct(lines.tecido, margin.revenue)}%</td>
                            </tr>
                            <tr>
                                <td className="omr-table-left">Aviamento</td>
                                <td>R$ {money(lines.aviamento)}</td>
                                <td>{pct(lines.aviamento, margin.revenue)}%</td>
                            </tr>
                            <tr>
                                <td className="omr-table-left">Mão de obra</td>
                                <td>R$ {money(lines.maoDeObra)}</td>
                                <td>{pct(lines.maoDeObra, margin.revenue)}%</td>
                            </tr>
                            <tr>
                                <td className="omr-table-left">Acessórios</td>
                                <td>R$ {money(lines.acessorios)}</td>
                                <td>{pct(lines.acessorios, margin.revenue)}%</td>
                            </tr>
                            <tr>
                                <td className="omr-table-left">Aditivos</td>
                                <td>R$ {money(lines.aditivos)}</td>
                                <td>{pct(lines.aditivos, margin.revenue)}%</td>
                            </tr>
                            <tr>
                                <td className="omr-table-left">Bordado</td>
                                <td>R$ {money(lines.bordado)}</td>
                                <td>{pct(lines.bordado, margin.revenue)}%</td>
                            </tr>
                            <tr>
                                <td className="omr-table-left">Estampa</td>
                                <td>R$ {money(lines.estampa)}</td>
                                <td>{pct(lines.estampa, margin.revenue)}%</td>
                            </tr>
                            <tr>
                                <td className="omr-table-left">Custos de produção lançados</td>
                                <td>R$ {money(lines.producao)}</td>
                                <td>{pct(lines.producao, margin.revenue)}%</td>
                            </tr>
                            <tr>
                                <td className="omr-table-left">Comissão</td>
                                <td>R$ {money(lines.comissao)}</td>
                                <td>{pct(lines.comissao, margin.revenue)}%</td>
                            </tr>
                            <tr className="omr-table-total">
                                <td className="omr-table-left">Custo total</td>
                                <td>R$ {money(margin.totalCost)}</td>
                                <td>{pct(margin.totalCost, margin.revenue)}%</td>
                            </tr>
                            <tr className="omr-table-total" style={{ color: isProfit ? PROFIT_COLOR : LOSS_COLOR }}>
                                <td className="omr-table-left">{isProfit ? 'Lucro' : 'Prejuízo'}</td>
                                <td>R$ {money(Math.abs(margin.profit))}</td>
                                <td>{pct(Math.abs(margin.profit), margin.revenue)}%</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <p className="omr-note">
                    Tecido e mão de obra usam o custo estimado do orçamento até a Ordem de Produção fechar o consumo de
                    material — a partir daí, tecido passa a usar o custo real dos lotes efetivamente consumidos. Aviamento só
                    aparece depois de consumido (não é precificado no orçamento). Bordado e estampa usam o valor lançado no
                    item do orçamento como custo. Custos de produção lançados incluem Facção Externa.
                </p>
            </div>
        </div>
    )
}

export default OrderMarginReport
